// File: src/components/pages/CancelForStudents.jsx
// Instructor page - Initiate cancellations on behalf of students
// UPDATED: Filters out bookings with status "cancelling"

import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Calendar, Clock, User, AlertCircle, CheckCircle, XCircle } from 'lucide-react';
import { bookingAPI, cancellationAPI } from '../../services/api';
import { AUTH0_NAMESPACE } from '../../utils/constants';
import '../../styles/modules.css';

export default function CancelForStudents() {
  const { user, getAccessTokenSilently } = useAuth0();
  
  const userRoles = user?.[`${AUTH0_NAMESPACE}/roles`] || [];
  const isServiceProvider = userRoles.includes('ServiceProvider');
  const instructorAuth0Id = user?.sub;

  const [bookings, setBookings] = useState([]);
  const [selectedBookings, setSelectedBookings] = useState(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    if (isServiceProvider && instructorAuth0Id) {
      loadInstructorBookings();
    }
  }, [isServiceProvider, instructorAuth0Id]);

  /**
   * Load all active bookings for the instructor
   * UPDATED: Filters out bookings with status "cancelling"
   */
  const loadInstructorBookings = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await bookingAPI.getInstructorBookings(getAccessTokenSilently);

      if (response.success && response.data) {
        // Filter out cancelled bookings AND bookings with status "cancelling"
        // Only show bookings with status "pending" or "booked" (active bookings)
        const activeBookings = response.data.filter(booking => 
          booking.status !== 'cancelled' && booking.status !== 'cancelling'
        );
        
        // Sort by date (earliest first), then by start time
        const sorted = activeBookings.sort((a, b) => {
          const dateCompare = new Date(a.date) - new Date(b.date);
          if (dateCompare !== 0) return dateCompare;
          
          // If same date, sort by start time
          return a.startTime.localeCompare(b.startTime);
        });
        
        setBookings(sorted);
        
        console.log(`Loaded ${sorted.length} active bookings (excluding cancelling status)`);
      } else {
        setBookings([]);
      }
    } catch (err) {
      console.error('Failed to load bookings:', err);
      setError('Failed to load bookings. Please try again.');
      setBookings([]);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Toggle booking selection
   */
  const toggleBookingSelection = (bookingId) => {
    setSelectedBookings(prev => {
      const newSet = new Set(prev);
      if (newSet.has(bookingId)) {
        newSet.delete(bookingId);
      } else {
        newSet.add(bookingId);
      }
      return newSet;
    });
  };

  /**
   * Select/Deselect all bookings
   */
  const toggleSelectAll = () => {
    if (selectedBookings.size === bookings.length) {
      setSelectedBookings(new Set());
    } else {
      setSelectedBookings(new Set(bookings.map(b => b.bookingId)));
    }
  };

  /**
   * Initiate cancellation for selected bookings
   * This creates cancellation requests that will appear in the Cancellations tab
   * and updates the booking status to "cancelling"
   */
  const handleInitiateCancellation = async () => {
    if (selectedBookings.size === 0) return;

    const confirmMessage = selectedBookings.size === 1
      ? 'Are you sure you want to initiate cancellation for this booking? The booking status will change to "cancelling" and you will need to approve it in the Cancellations tab.'
      : `Are you sure you want to initiate cancellation for ${selectedBookings.size} bookings? The booking statuses will change to "cancelling" and you will need to approve them in the Cancellations tab.`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setIsProcessing(true);
    setError(null);
    setSuccess(null);

    try {
      // Get the selected booking details
      const selectedBookingDetails = bookings.filter(b => selectedBookings.has(b.bookingId));

      // Prepare cancellation requests
      const cancellations = selectedBookingDetails.map(booking => ({
        bookingId: booking.bookingId,
        providerId: booking.providerId,
        instructorPhone: booking.providerPhone,
        studentPhone: booking.studentPhone,
        date: booking.date,
        startTime: booking.startTime,
        endTime: booking.endTime,
        studentName: booking.studentName,
        providerName: booking.providerName,
        reason: 'Cancelled by instructor',
        initiatedBy: 'instructor' // Flag to indicate this was initiated by instructor
      }));

      // Call the instructor-specific cancellation endpoint
      const response = await cancellationAPI.createInstructorCancellationRequests(
        { cancellations },
        getAccessTokenSilently
      );

      if (response.success) {
        const { successCount, failCount } = response.data;
        
        if (failCount === 0) {
          setSuccess(
            `Successfully initiated ${successCount} cancellation request${successCount > 1 ? 's' : ''}! ` +
            `The booking${successCount > 1 ? 's' : ''} status changed to "cancelling". ` +
            `Please go to the Cancellations tab to approve ${successCount > 1 ? 'them' : 'it'}.`
          );
        } else {
          setSuccess(
            `Initiated ${successCount} cancellation request${successCount > 1 ? 's' : ''}. ${failCount} failed. ` +
            `Please check the Cancellations tab.`
          );
        }

        // Clear selections and reload bookings
        setSelectedBookings(new Set());
        await loadInstructorBookings();

        // Auto-hide success message after 8 seconds
        setTimeout(() => setSuccess(null), 8000);
      }
    } catch (err) {
      console.error('Cancellation initiation error:', err);
      setError(`Failed to initiate cancellations: ${err.message}`);
      setTimeout(() => setError(null), 5000);
    } finally {
      setIsProcessing(false);
    }
  };

  /**
   * Format date for display
   */
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { 
      weekday: 'short', 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  /**
   * Format time from 24-hour to 12-hour format
   */
  const formatTime = (time24) => {
    const [hour, min] = time24.split(':').map(Number);
    const period = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${min.toString().padStart(2, '0')} ${period}`;
  };

  // Access control
  if (!isServiceProvider) {
    return (
      <div className="tab-content-placeholder">
        <h2>Access Denied</h2>
        <p>This page is only accessible to instructors</p>
      </div>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="bookings-container">
        <div className="loading-state">
          <Clock size={48} className="loading-icon" />
          <h2>Loading bookings...</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="bookings-container">
      {/* Success/Error Messages */}
      {success && (
        <div className="booking-success-banner">
          <CheckCircle size={24} />
          <span>{success}</span>
        </div>
      )}

      {error && (
        <div className="booking-error-banner">
          <AlertCircle size={24} />
          <span>{error}</span>
        </div>
      )}

      {/* Header with Actions */}
      <div className="cancellations-header">
        <h2>Cancel For Students</h2>
        
        <div className="header-actions">
          {/* Select All Checkbox */}
          {bookings.length > 0 && (
            <div className="select-all-wrapper">
              <input
                type="checkbox"
                id="select-all"
                checked={selectedBookings.size === bookings.length && bookings.length > 0}
                onChange={toggleSelectAll}
              />
              <label htmlFor="select-all">
                Select All ({bookings.length})
              </label>
            </div>
          )}

          {/* Initiate Cancellation Button */}
          {selectedBookings.size > 0 && (
            <button 
              className="btn-approve"
              onClick={handleInitiateCancellation}
              disabled={isProcessing}
            >
              {isProcessing 
                ? 'Processing...' 
                : `Initiate Cancellation (${selectedBookings.size})`
              }
            </button>
          )}
        </div>
      </div>

      {/* Information Banner */}
      <div className="info-banner">
        <AlertCircle size={20} />
        <span>
          Select bookings to initiate cancellation. The booking status will change to <strong>"cancelling"</strong> and 
          the slot will remain booked until you approve the cancellation in the <strong>Cancellations tab</strong>.
          Bookings with "cancelling" status are not shown here.
        </span>
      </div>

      {/* Bookings List */}
      {bookings.length === 0 ? (
        <div className="empty-state">
          <AlertCircle size={64} className="empty-icon" />
          <h3>No Active Bookings</h3>
          <p>
            There are no active bookings available to cancel. 
            Bookings with "cancelling" status are shown in the Cancellations tab.
          </p>
        </div>
      ) : (
        <div className="cancellation-requests-list">
          {bookings.map((booking) => {
            const isSelected = selectedBookings.has(booking.bookingId);

            return (
              <div 
                key={booking.bookingId} 
                className={`cancellation-card ${isSelected ? 'cancellation-card-selected' : ''}`}
              >
                {/* Checkbox */}
                <div className="cancellation-checkbox">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleBookingSelection(booking.bookingId)}
                  />
                </div>

                {/* Booking Details */}
                <div className="cancellation-details">
                  {/* Header Row - Student Name */}
                  <div className="cancellation-header-row">
                    <div className="student-info">
                      <User size={18} />
                      <span className="student-name">{booking.studentName}</span>
                    </div>
                    <span className="status-badge status-active">
                      {booking.status || 'Active'}
                    </span>
                  </div>

                  {/* Booking Date and Time */}
                  <div className="booking-info-row">
                    <div className="booking-date">
                      <Calendar size={18} />
                      <span>{formatDate(booking.date)}</span>
                    </div>
                    <div className="booking-time">
                      <Clock size={18} />
                      <span>
                        {formatTime(booking.startTime)} - {formatTime(booking.endTime)}
                      </span>
                    </div>
                  </div>

                  {/* Booking ID (for reference) */}
                  <div className="booking-metadata">
                    <span className="metadata-item" style={{ fontSize: '0.85rem', color: '#666' }}>
                      {booking.studentName} - {booking.studentEmail} - {booking.studentPhone}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Footer Info */}
      {bookings.length > 0 && (
        <div className="bookings-footer-info">
          <p>
            Total Active Bookings: <strong>{bookings.length}</strong>
            {selectedBookings.size > 0 && (
              <span> | Selected: <strong>{selectedBookings.size}</strong></span>
            )}
          </p>
          <p style={{ fontSize: '0.9rem', color: '#666', marginTop: '8px' }}>
            Bookings with "cancelling" status are not shown here. Check the Cancellations tab.
          </p>
        </div>
      )}
    </div>
  );
}